import {List, ListItem} from '@common/ui/list/list';
import {ListItemBase} from '@common/ui/list/list-item-base';
import {Link} from 'react-router-dom';
import {PersonIcon} from '@common/icons/material/Person';
import {Trans} from '@common/i18n/trans';
import {LoginIcon} from '@common/icons/material/Login';
import {LockIcon} from '@common/icons/material/Lock';
import {EmailIcon} from '@common/icons/material/Email';
import {PhonelinkLockIcon} from '@common/icons/material/PhonelinkLock';
import {LanguageIcon} from '@common/icons/material/Language';
import {ApiIcon} from '@common/icons/material/Api';
import {DangerousIcon} from '@common/icons/material/Dangerous';
import {ChatIcon} from '@common/icons/material/Chat';
import {ReactNode, useContext} from 'react';
import {DevicesIcon} from '@common/icons/material/Devices';
import {HistoryIcon} from '@common/icons/material/History';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@common/core/settings/use-settings';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
// import {NotificationsIcon} from '@common/icons/material/Notifications';
import {TelegramIcon} from '@common/icons/social/telegram';
import {scrollToAccountSettingsPanel} from '@common/auth/ui/account-settings/scroll-to-account-settings-panel';

export enum AccountSettingsId {
  AccountDetails = 'account-details',
  SocialLogin = 'social-login',
  Email = 'email',
  Password = 'password',
  TwoFactor = 'two-factor',
  LocationAndLanguage = 'location-and-language',
  Developers = 'developers',
  DeleteAccount = 'delete-account',
  Sessions = 'sessions',
  NotificationChannel = 'notification-channel',
  VkConnection = 'vk-connection',
  TelegramConnection = 'telegram-connection',
}

/**
 * Боковое меню настроек аккаунта.
 * Изменено: 2026-04-16 — пункт «История транзакций кредитов» ведёт на отдельную страницу.
 */
export function AccountSettingsSidenav() {
  const p = AccountSettingsId;

  const {hasPermission} = useAuth();
  const {api, social} = useSettings();
  const {auth} = useContext(SiteConfigContext);

  const socialEnabled =
    social?.envato || social?.google || social?.facebook || social?.twitter;

  return (
    <aside className="sticky top-10 hidden flex-shrink-0 lg:block">
      <List padding="p-0">
        {auth.accountSettingsPanels?.map(panel => (
          <Item
            key={panel.id}
            icon={<panel.icon viewBox="0 0 50 50" />}
            panel={panel.id as AccountSettingsId}
          >
            <Trans {...panel.label} />
          </Item>
        ))}
        <Item icon={<PersonIcon />} panel={p.AccountDetails}>
          <Trans message="Account details" />
        </Item>
        {socialEnabled && (
          <Item icon={<LoginIcon />} panel={p.SocialLogin}>
            <Trans message="Social login" />
          </Item>
        )}
        <Item icon={<EmailIcon />} panel={p.Email}>
          <Trans message="Email" />
        </Item>
        <Item icon={<LockIcon />} panel={p.Password}>
          <Trans message="Password" />
        </Item>
        <Item icon={<PhonelinkLockIcon />} panel={p.TwoFactor}>
          <Trans message="Two factor authentication" />
        </Item>
        <Item icon={<DevicesIcon />} panel={p.Sessions}>
          <Trans message="Active sessions" />
        </Item>
        {/* <Item icon={<NotificationsIcon />} panel={p.NotificationChannel}>
          <Trans message="Notification channel" />
        </Item> */}
        <Item icon={<ChatIcon />} panel={p.VkConnection}>
          <Trans message="VK group connection" />
        </Item>
        <Item icon={<TelegramIcon />} panel={p.TelegramConnection}>
          <Trans message="Telegram chats" />
        </Item>
        <li className="mb-10">
          <ListItemBase
            elementType={Link}
            to="/account-settings/credits/transactions"
            startIcon={<HistoryIcon />}
            className="rounded"
          >
            <Trans message="Credits transaction history" />
          </ListItemBase>
        </li>
        <Item icon={<LanguageIcon />} panel={p.LocationAndLanguage}>
          <Trans message="Location and language" />
        </Item>
        {api?.integrated && hasPermission('api.access') ? (
          <Item icon={<ApiIcon />} panel={p.Developers}>
            <Trans message="Developers" />
          </Item>
        ) : null}
        <Item icon={<DangerousIcon />} panel={p.DeleteAccount}>
          <Trans message="Delete account" />
        </Item>
      </List>
    </aside>
  );
}

interface ItemProps {
  children: ReactNode;
  icon: ReactNode;
  isLast?: boolean;
  panel: AccountSettingsId;
}
function Item({children, icon, isLast, panel}: ItemProps) {
  return (
    <ListItem
      startIcon={icon}
      className={isLast ? undefined : 'mb-10'}
      onSelected={() => {
        scrollToAccountSettingsPanel(panel);
      }}
    >
      {children}
    </ListItem>
  );
}
